/**
 * Monster Factions for thematically consistent encounter generation
 * 
 * Key concepts:
 * - Monsters can belong to MULTIPLE factions
 * - Factions belong to GROUPS for soft compatibility
 * - Compatibility scoring instead of hard filtering
 */

// Faction groups for soft compatibility
export const FACTION_GROUPS = {
  humanoid: ['goblinoid', 'orc', 'drow', 'kobold', 'gnoll', 'bandit', 'cult', 'lizardfolk', 'sahuagin'],
  undead: ['undead'],
  fiend: ['demon', 'devil'],
  beast: ['wolves', 'bears', 'spiders', 'snakes', 'swarm', 'aquatic'],
  monster: ['giant', 'dragon', 'aberration', 'hag', 'yuan_ti', 'lycanthrope'],
  nature: ['elemental', 'fey', 'plant'],
  construct: ['construct', 'ooze'],
};

// Build reverse lookup: faction -> group
const FACTION_TO_GROUP = {};
Object.entries(FACTION_GROUPS).forEach(([group, factions]) => {
  factions.forEach(faction => {
    FACTION_TO_GROUP[faction] = group;
  });
});

// Factions with metadata
export const FACTIONS = {
  goblinoid: {
    label: 'Goblinoid Warband',
    description: 'Goblins, hobgoblins, and bugbears working together',
    group: 'humanoid',
    allowedAllies: ['wolves', 'beast'],
    preferredTactics: ['raidingParty', 'huntingPack', 'bossWithAllies'],
  },
  orc: {
    label: 'Orc Horde',
    description: 'Orcs and their ogre allies',
    group: 'humanoid',
    allowedAllies: ['wolves', 'giant'],
    preferredTactics: ['bruteHorde', 'raidingParty', 'bossWithAllies'],
  },
  drow: {
    label: 'Drow Raiding Party',
    description: 'Dark elves and their spider servants',
    group: 'humanoid',
    allowedAllies: ['spiders', 'demon'],
    preferredTactics: ['sneakStrike', 'raidingParty', 'bossWithAllies'],
  },
  kobold: {
    label: 'Kobold Tribe',
    description: 'Kobolds and their draconic kin',
    group: 'humanoid',
    allowedAllies: ['dragon'],
    preferredTactics: ['koboldTrapTeam', 'raidingParty', 'ambushLurkers', 'swarm'],
  },
  gnoll: {
    label: 'Gnoll Pack',
    description: 'Savage hyena-like hunters',
    group: 'humanoid',
    allowedAllies: ['wolves'],
    preferredTactics: ['huntingPack', 'bruteHorde', 'swarm'],
  },
  bandit: {
    label: 'Bandit Gang',
    description: 'Human outlaws and mercenaries',
    group: 'humanoid',
    allowedAllies: [],
    preferredTactics: ['raidingParty', 'ambushLurkers'],
  },
  cult: {
    label: 'Evil Cult',
    description: 'Cultists and their summoned allies',
    group: 'humanoid',
    allowedAllies: ['demon', 'devil', 'undead'],
    preferredTactics: ['bossLeader', 'mixedHorde', 'sneakStrike'],
  },
  lizardfolk: {
    label: 'Lizardfolk Tribe',
    description: 'Lizardfolk and swamp creatures',
    group: 'humanoid',
    allowedAllies: ['snakes', 'aquatic'],
    preferredTactics: ['raidingParty', 'ambushLurkers'],
  },
  sahuagin: {
    label: 'Sahuagin Raiders',
    description: 'Sea devils and aquatic monsters',
    group: 'humanoid',
    allowedAllies: ['aquatic'],
    preferredTactics: ['raidingParty', 'ambushLurkers', 'mixedHorde'],
  },
  undead: {
    label: 'Undead Horde',
    description: 'Skeletons, zombies, and spirits',
    group: 'undead',
    allowedAllies: [],
    preferredTactics: ['undeadHorde', 'bruteHorde', 'bossLoneMonster', 'swarm'],
  },
  demon: {
    label: 'Demonic Incursion',
    description: 'Chaotic demons from the Abyss',
    group: 'fiend',
    allowedAllies: ['devil'],
    preferredTactics: ['demonIncursion', 'bossWithAllies'],
  },
  devil: {
    label: 'Infernal Legion',
    description: 'Lawful devils from the Nine Hells',
    group: 'fiend',
    allowedAllies: [],
    preferredTactics: ['bossLeader', 'mixedHorde'],
  },
  fey: {
    label: 'Fey Court',
    description: 'Mischievous creatures of the Feywild',
    group: 'nature',
    allowedAllies: ['plant'],
    preferredTactics: ['feyCourt', 'sneakStrike', 'ambushLurkers'],
  },
  plant: {
    label: 'Corrupted Grove',
    description: 'Awakened plants and blights',
    group: 'nature',
    allowedAllies: ['fey'],
    preferredTactics: ['feyCourt', 'mixedHorde'],
  },
  dragon: {
    label: "Dragon's Servants",
    description: 'Dragons and draconic creatures',
    group: 'monster',
    allowedAllies: ['kobold'],
    preferredTactics: ['bossLoneMonster', 'bossWithAllies'],
  },
  giant: {
    label: 'Giant Warband',
    description: 'Giants and their servants',
    group: 'monster',
    allowedAllies: ['orc', 'wolves'],
    preferredTactics: ['bruteHorde', 'bossLeader'],
  },
  aberration: {
    label: 'Eldritch Brood',
    description: 'Mind flayers, beholders, and warped creatures',
    group: 'aberration',
    allowedAllies: ['humanoid'],
    preferredTactics: ['psychicControl', 'bossWithAllies', 'sneakStrike'],
  },
  construct: {
    label: 'Ancient Constructs',
    description: 'Golems, animated armor, and magical machines',
    group: 'construct',
    allowedAllies: [],
    preferredTactics: ['relentlessAdvance', 'bossLoneMonster'],
  },
  elemental: {
    label: 'Elemental Forces',
    description: 'Fire, water, air, and earth elementals',
    group: 'elemental',
    allowedAllies: [],
    preferredTactics: ['environmentalHazard', 'mixedHorde'],
  },
  ooze: {
    label: 'Ooze Colony',
    description: 'Slimes, jellies, and dissolving horrors',
    group: 'ooze',
    allowedAllies: [],
    preferredTactics: ['engulfingSwarm', 'bossLoneMonster', 'swarm'],
  },
  dragonCult: {
    label: 'Dragon Cult',
    description: 'Fanatics serving dragons',
    group: 'humanoid',
    allowedAllies: ['dragon'],
    preferredTactics: ['bossLeader', 'dragonWorship'],
  },
  elf: {
    label: 'Elven Warband',
    description: 'Graceful and deadly forest fighters',
    group: 'humanoid',
    allowedAllies: ['fey', 'beast'],
    preferredTactics: ['precisionStrike', 'sneakStrike'],
  },
  dwarf: {
    label: 'Dwarven Defenders',
    description: 'Heavily armored defensive fighters',
    group: 'humanoid',
    allowedAllies: [],
    preferredTactics: ['fortifiedLine', 'bossLeader'],
  },
  human: {
    label: 'Military Company',
    description: 'Trained soldiers and officers',
    group: 'humanoid',
    allowedAllies: [],
    preferredTactics: ['disciplinedFormation', 'bossLeader'],
  },
  shapechanger: {
    label: 'Hidden Infiltrators',
    description: 'Doppelgangers and disguised threats',
    group: 'humanoid',
    allowedAllies: [],
    preferredTactics: ['infiltrationStrike', 'sneakStrike'],
  },
  merfolk: {
    label: 'Merfolk Tide',
    description: 'Aquatic defenders of the seas',
    group: 'humanoid',
    allowedAllies: ['elemental'],
    preferredTactics: ['mixedHorde', 'environmentalHazard'],
  },
  titan: {
    label: 'Titanic Forces',
    description: 'Mythic giants and world-shakers',
    group: 'giant',
    allowedAllies: [],
    preferredTactics: ['bossLoneMonster', 'relentlessAdvance'],
  },
  beast: {
    label: 'Wild Pack',
    description: 'Natural predators hunting together',
    group: 'beast',
    allowedAllies: [],
    preferredTactics: ['huntingPack', 'swarm'],
  },
};

// Patterns that factions prefer
export const TACTICAL_PATTERNS = {
  // ======================
  // Boss-style encounters
  // ======================
  bossLeader: {
    description: 'A single strong leader with minions.',
    patterns: [
      {
        name: 'Commanding Presence',
        core: ['leader'],
        support: ['brute'],
        flex: ['soldier', 'artillery'],
      },
      {
        name: 'Coordinated Strike',
        core: ['leader'],
        support: ['skirmisher'],
        flex: ['lurker', 'artillery'],
      },
      {
        name: 'Dark Ritual',
        core: ['leader'],
        support: ['minion'],
        flex: ['controller', 'spellcaster'],
      },
      {
        name: 'Overwhelming Force',
        core: ['brute'],
        support: ['soldier'],
        flex: ['skirmisher'],
      },
      {
        name: 'Siege Pressure',
        core: ['brute'],
        support: ['artillery'],
        flex: ['soldier'],
      },
    ]
  },
  bossLoneMonster: {
    description: 'One powerful creature dominates the battlefield.',
    patterns: [
      { name: 'Rampaging Beast', core: ['brute'] },
      { name: 'Tactical Commander', core: ['leader'] },
      { name: 'Deadly Hunter', core: ['skirmisher', 'lurker'] },
      { name: 'Shadow Stalker', core: ['lurker'] },
      { name: 'Arcane Threat', core: ['spellcaster', 'controller'] },
    ]
  },
  bossWithAllies: {
    description: 'A boss supported by smaller allies.',
    patterns: [
      {
        name: 'Balanced Warband',
        core: ['leader'],
        support: ['brute', 'soldier'],
        flex: ['artillery'],
      },
      {
        name: 'Control Squad',
        core: ['leader'],
        support: ['controller'],
        flex: ['minion', 'soldier'],
      },
      {
        name: 'Ambush Command',
        core: ['leader'],
        support: ['skirmisher', 'lurker'],
        flex: ['artillery'],
      },
    ]
  },

  // ======================
  // Ambush-style encounters
  // ======================
  huntingPack: {
    description: 'Coordinated attack from a small group.',
    patterns: [
      {
        name: 'Pack Hunters',
        core: ['skirmisher'],
        support: ['lurker'],
        flex: ['skirmisher', 'minion'],
      },
      {
        name: 'Encircle and Isolate',
        core: ['lurker'],
        support: ['skirmisher'],
        flex: ['lurker'],
      },
    ]
  },
  raidingParty: {
    description: 'Humanoids with small allies (pets or beasts).',
    patterns: [
      {
        name: 'Disciplined Raid',
        core: ['leader'],
        support: ['soldier'],
        flex: ['artillery', 'skirmisher'],
      },
      {
        name: 'Beast-Assisted Assault',
        core: ['soldier'],
        support: ['skirmisher'],
        flex: ['brute', 'minion'],
      },
    ]
  },
  sneakStrike: {
    description: 'A mix of lurkers and artillery hitting from shadows.',
    patterns: [
      {
        name: 'Shadow Ambush',
        core: ['lurker'],
        support: ['artillery'],
        flex: ['skirmisher'],
      },
      {
        name: 'Hit and Fade',
        core: ['skirmisher'],
        support: ['lurker'],
        flex: ['artillery'],
      },
    ]
  },

  // ======================
  // Waves/Horde-style encounters
  // ======================
  bruteHorde: {
    description: 'Many strong creatures, tough to bring down.',
    patterns: [
      {
        name: 'Crushing Advance',
        core: ['brute'],
        support: ['soldier'],
        flex: ['brute'],
      },
      {
        name: 'Relentless Pressure',
        core: ['brute'],
        support: ['brute'],
        flex: ['soldier'],
      },
    ]
  },
  swarm: {
    description: 'Many weak creatures, overwhelming in numbers.',
    patterns: [
      {
        name: 'Overwhelming Numbers',
        core: ['minion'],
        support: ['minion'],
        flex: ['skirmisher'],
      },
      {
        name: 'Chittering Mass',
        core: ['skirmisher'],
        support: ['minion'],
        flex: ['minion'],
      },
    ]
  },
  mixedHorde: {
    description: 'Several allied factions attacking together.',
    patterns: [
      {
        name: 'Chaotic Assault',
        core: ['brute'],
        support: ['skirmisher'],
        flex: ['artillery'],
      },
      {
        name: 'Layered Attack',
        core: ['soldier'],
        support: ['artillery'],
        flex: ['brute', 'skirmisher'],
      },
    ]
  },

  // ======================
  // Theme-specific flavor patterns
  // Require matching factions to be labeled with these tactics
  // ======================
  goblinoidWarband: {
    description: 'Goblins + hobgoblins + bugbears, optionally with wolves.',
    factions: ['goblinoid'],
    patterns: [
      {
        name: 'Coordinated Strike',
        core: ['leader'],
        support: ['skirmisher'],
        flex: ['brute', 'minion'],
      },
      {
        name: 'Mob Tactics',
        core: ['skirmisher'],
        support: ['minion'],
        flex: ['skirmisher'],
      },
    ]
  },
  orcHorde: {
    description: 'Orcs and ogres, optional wolf allies.',
    factions: ['orc'],
    patterns: [
      {
        name: 'Brutal Charge',
        core: ['brute'],
        support: ['soldier'],
        flex: ['brute'],
      },
      {
        name: 'Savage Overrun',
        core: ['brute'],
        support: ['skirmisher'],
        flex: ['minion'],
      },
    ]
  },
  drowRaid: {
    description: 'Drow with spider minions.',
    factions: ['drow'],
    patterns: [
      {
        name: 'Web Ambush',
        core: ['lurker'],
        support: ['artillery'],
        flex: ['controller'],
      },
      {
        name: 'Poisoned Volley',
        core: ['artillery'],
        support: ['lurker'],
        flex: ['skirmisher'],
      },
    ]
  },
  koboldTrapTeam: {
    description: 'Kobolds and dragon allies set up a trap.',
    factions: ['kobold'],
    patterns: [
      {
        name: 'Trap Gauntlet',
        core: ['artillery'],
        support: ['skirmisher'],
        flex: ['minion'],
      },
      {
        name: 'Dragon’s Servants',
        core: ['leader'],
        support: ['skirmisher'],
        flex: ['artillery'],
      },
    ]
  },
  undeadHorde: {
    description: 'Skeletons, zombies, and spirits overwhelm the party.',
    factions: ['undead'],
    patterns: [
      {
        name: 'Endless Dead',
        core: ['minion'],
        support: ['brute'],
        flex: ['minion'],
      },
      {
        name: 'Grave Surge',
        core: ['brute'],
        support: ['minion'],
        flex: ['controller'],
      },
    ]
  },
  demonIncursion: {
    description: 'Demons with devil allies create chaos.',
    factions: ['demon', 'devil'],
    patterns: [
      {
        name: 'Chaotic Slaughter',
        core: ['brute'],
        support: ['controller'],
        flex: ['artillery'],
      },
      {
        name: 'Hellfire Assault',
        core: ['artillery'],
        support: ['brute'],
        flex: ['controller'],
      },
    ]
  },
  feyCourt: {
    description: 'Fey with plants or summoned minions to disrupt the party.',
    factions: ['fey'],
    patterns: [
      {
        name: 'Trickster Control',
        core: ['controller'],
        support: ['skirmisher'],
        flex: ['lurker'],
      },
      {
        name: 'Enchanted Ambush',
        core: ['lurker'],
        support: ['controller'],
        flex: ['skirmisher'],
      },
    ]
  },
  psychicControl: {
    description: 'Enemies dominate minds and disrupt actions.',
    patterns: [
      {
        name: 'Mind Domination',
        core: ['controller'],
        support: ['lurker'],
        flex: ['minion'],
      },
      {
        name: 'Synaptic Overload',
        core: ['controller'],
        support: ['artillery'],
        flex: ['spellcaster'],
      },
    ]
  },
  relentlessAdvance: {
    description: 'Enemies march forward without stopping.',
    patterns: [
      {
        name: 'Unstoppable March',
        core: ['brute'],
        support: ['soldier'],
        flex: ['brute'],
      },
    ]
  },
  environmentalHazard: {
    description: 'The battlefield itself is dangerous.',
    patterns: [
      {
        name: 'Burning Ground',
        core: ['controller'],
        support: ['artillery'],
        flex: ['minion'],
      },
    ]
  },
  engulfingSwarm: {
    description: 'Enemies overwhelm and consume targets.',
    patterns: [
      {
        name: 'Engulf',
        core: ['brute'],
        support: ['minion'],
        flex: ['controller'],
      },
    ]
  },
  fortifiedLine: {
    description: 'Defensive formation that is hard to break.',
    patterns: [
      {
        name: 'Shield Wall',
        core: ['soldier'],
        support: ['leader'],
        flex: ['artillery'],
      },
    ]
  },
  precisionStrike: {
    description: 'Highly accurate and coordinated attacks.',
    patterns: [
      {
        name: 'Focused Fire',
        core: ['artillery'],
        support: ['leader'],
        flex: ['skirmisher'],
      },
    ]
  },
  dragonWorship: {
    description: 'Followers empower a dragon or draconic boss.',
    patterns: [
      {
        name: 'Empowered Dragon',
        core: ['leader'],
        support: ['minion'],
        flex: ['artillery'],
      },
    ]
  },
  infiltrationStrike: {
    description: 'Enemies begin hidden among allies.',
    patterns: [
      {
        name: 'Hidden Threat',
        core: ['lurker'],
        support: ['controller'],
        flex: ['skirmisher'],
      },
    ]
  },
  disciplinedFormation: {
    description: 'Highly coordinated tactical combat.',
    patterns: [
      {
        name: 'Phalanx',
        core: ['soldier'],
        support: ['leader'],
        flex: ['artillery'],
      },
    ]
  }

  
};

const ENCOUNTER_PATTERNS_PER_TYPE = {
  boss: [
    TACTICAL_PATTERNS.bossLeader,
    TACTICAL_PATTERNS.bossLoneMonster,
    TACTICAL_PATTERNS.bossWithAllies,
    TACTICAL_PATTERNS.relentlessAdvance,     // constructs, titans, undead elites
    TACTICAL_PATTERNS.psychicControl,        // aberrations
    TACTICAL_PATTERNS.dragonWorship,         // dragon + cult synergy
    TACTICAL_PATTERNS.fortifiedLine,         // defensive boss fights
  ],

  ambush: [
    TACTICAL_PATTERNS.huntingPack,
    TACTICAL_PATTERNS.raidingParty,
    TACTICAL_PATTERNS.sneakStrike,
    TACTICAL_PATTERNS.goblinoidWarband,
    TACTICAL_PATTERNS.orcHorde,
    TACTICAL_PATTERNS.drowRaid,
    TACTICAL_PATTERNS.koboldTrapTeam,
    TACTICAL_PATTERNS.demonIncursion,
    TACTICAL_PATTERNS.feyCourt,
    TACTICAL_PATTERNS.infiltrationStrike,    // shapechangers / hidden enemies
    TACTICAL_PATTERNS.precisionStrike,       // elves / elite ranged
    TACTICAL_PATTERNS.psychicControl,        // mind games before combat fully starts
  ],

  waves: [
    TACTICAL_PATTERNS.bruteHorde,
    TACTICAL_PATTERNS.swarm,
    TACTICAL_PATTERNS.mixedHorde,
    TACTICAL_PATTERNS.undeadHorde,
    TACTICAL_PATTERNS.demonIncursion,
    TACTICAL_PATTERNS.feyCourt,
    TACTICAL_PATTERNS.relentlessAdvance,     // slow unstoppable enemies
    TACTICAL_PATTERNS.environmentalHazard,   // terrain + spawning threats
    TACTICAL_PATTERNS.engulfingSwarm,        // oozes / consuming enemies
    TACTICAL_PATTERNS.disciplinedFormation,  // military waves
  ],
};

// Map subtypes to factions
const SUBTYPE_TO_FACTIONS = {
  goblinoid: ['goblinoid'],
  orc: ['orc'],
  gnoll: ['gnoll'],
  kobold: ['kobold'],
  demon: ['demon'],
  devil: ['devil'],
  elf: ['drow'], // Drow specifically
  lizardfolk: ['lizardfolk'],
  sahuagin: ['sahuagin'],
  shapechanger: [], // Too varied
};

// Map types to factions
const TYPE_TO_FACTIONS = {
  undead: ['undead'],
  elemental: ['elemental'],
  fey: ['fey'],
  construct: ['construct'],
  ooze: ['ooze'],
  plant: ['plant'],
  giant: ['giant'],
  dragon: ['dragon'],
  aberration: ['aberration'],
};

// Explicit monster-to-factions mappings (monsters can have multiple factions)
const MONSTER_FACTIONS = {
  // Goblinoid faction + allies
  goblin: ['goblinoid'],
  hobgoblin: ['goblinoid'],
  bugbear: ['goblinoid'],
  worg: ['goblinoid', 'wolves'], // Can be with goblins OR wolf packs
  
  // Orc faction
  orc: ['orc'],
  ogre: ['orc', 'giant'],
  'half-ogre': ['orc', 'giant'],
  
  // Drow faction
  drow: ['drow'],
  drider: ['drow', 'spiders'],
  'giant-spider': ['drow', 'spiders'], // Both drow servants and wild spiders
  'phase-spider': ['spiders'],
  
  // Kobold faction
  kobold: ['kobold'],
  pseudodragon: ['kobold', 'dragon'],
  
  // Gnoll faction
  gnoll: ['gnoll'],
  hyena: ['gnoll', 'wolves'],
  'giant-hyena': ['gnoll', 'wolves'],
  
  // Bandit/Human faction
  bandit: ['bandit'],
  'bandit-captain': ['bandit'],
  thug: ['bandit'],
  spy: ['bandit'],
  assassin: ['bandit'],
  scout: ['bandit'],
  veteran: ['bandit'],
  knight: ['bandit'],
  
  // Cult faction
  cultist: ['cult'],
  'cult-fanatic': ['cult'],
  mage: ['cult', 'bandit'],
  priest: ['cult'],
  acolyte: ['cult'],
  
  // Wolf pack
  wolf: ['wolves'],
  'dire-wolf': ['wolves'],
  'winter-wolf': ['wolves'],
  
  // Bear faction
  'black-bear': ['bears'],
  'brown-bear': ['bears'],
  'polar-bear': ['bears'],
  owlbear: ['bears'],
  
  // Spider faction  
  spider: ['spiders'],
  'giant-wolf-spider': ['spiders'],
  ettercap: ['spiders', 'drow'],
  
  // Snake faction
  'constrictor-snake': ['snakes'],
  'giant-constrictor-snake': ['snakes'],
  'poisonous-snake': ['snakes'],
  'giant-poisonous-snake': ['snakes'],
  'flying-snake': ['snakes'],
  'swarm-of-poisonous-snakes': ['snakes', 'swarm'],
  
  // Swarm faction
  'swarm-of-rats': ['swarm'],
  'swarm-of-bats': ['swarm'],
  'swarm-of-insects': ['swarm'],
  'swarm-of-beetles': ['swarm'],
  'swarm-of-centipedes': ['swarm'],
  'swarm-of-spiders': ['swarm', 'spiders'],
  'swarm-of-wasps': ['swarm'],
  'giant-rat': ['swarm'],
  'giant-centipede': ['swarm'],
  
  // Aquatic faction
  'giant-shark': ['aquatic'],
  'hunter-shark': ['aquatic'],
  'reef-shark': ['aquatic'],
  'giant-octopus': ['aquatic'],
  'giant-crab': ['aquatic'],
  'swarm-of-quippers': ['aquatic', 'swarm'],
  sahuagin: ['sahuagin', 'aquatic'],
  merrow: ['aquatic'],
  
  // Lizardfolk
  lizardfolk: ['lizardfolk'],
  
  // Lycanthrope faction
  'werewolf-wolf-form': ['lycanthrope', 'wolves'],
  'werewolf-hybrid-form': ['lycanthrope'],
  'werewolf-human-form': ['lycanthrope'],
  'wererat-human-form': ['lycanthrope'],
  'wererat-hybrid-form': ['lycanthrope'],
  'werebear-human-form': ['lycanthrope'],
  'werebear-hybrid-form': ['lycanthrope', 'bears'],
  'werebear-bear-form': ['bears'],
  'wereboar-human-form': ['lycanthrope'],
  'wereboar-hybrid-form': ['lycanthrope'],
  'weretiger-human-form': ['lycanthrope'],
  'weretiger-hybrid-form': ['lycanthrope'],
  
  // Hag faction
  'green-hag': ['hag', 'fey'],
  'sea-hag': ['hag', 'fey', 'aquatic'],
  'night-hag': ['hag', 'fiend'],
  
  // Yuan-ti faction
  'yuan-ti-pureblood': ['yuan_ti'],
  'yuan-ti-malison': ['yuan_ti'],
  'yuan-ti-abomination': ['yuan_ti'],
  
  // Hell Hound goes with devils
  'hell-hound': ['devil', 'wolves'],
  
  // Undead specifics
  skeleton: ['undead'],
  zombie: ['undead'],
  ghoul: ['undead'],
  ghast: ['undead'],
  wight: ['undead'],
  wraith: ['undead'],
  specter: ['undead'],
  shadow: ['undead'],
  ghost: ['undead'],
  mummy: ['undead'],
  'mummy-lord': ['undead'],
  vampire: ['undead'],
  'vampire-spawn': ['undead'],
  lich: ['undead'],
  'ogre-zombie': ['undead'],
  'minotaur-skeleton': ['undead'],
};

/**
 * Get all factions for a monster (returns array)
 * @param {Object} monster - Monster object with index, type, subtype
 * @returns {string[]} Array of faction keys
 */
export function getMonsterFactions(monster) {
  if (!monster) return [];
  
  // 1. Check explicit mapping first
  if (MONSTER_FACTIONS[monster.index]) {
    return MONSTER_FACTIONS[monster.index];
  }
  
  // 2. Check subtype
  if (monster.subtype && SUBTYPE_TO_FACTIONS[monster.subtype]) {
    const factions = SUBTYPE_TO_FACTIONS[monster.subtype];
    if (factions.length > 0) return factions;
  }
  
  // 3. Check type
  if (monster.type && TYPE_TO_FACTIONS[monster.type]) {
    return TYPE_TO_FACTIONS[monster.type];
  }
  
  return [];
}

/**
 * Get compatibility score between two monsters
 * @returns {number} 0 = incompatible, 1 = same group, 2 = allied factions, 3 = same faction
 */
export function getFactionCompatibilityScore(monster1, monster2) {
  const factions1 = getMonsterFactions(monster1);
  const factions2 = getMonsterFactions(monster2);
  
  // No factions = low affinity (not a free pass)
  if (factions1.length === 0 || factions2.length === 0) return 0.5;
  
  // Check for same faction (highest compatibility)
  for (const f1 of factions1) {
    if (factions2.includes(f1)) return 3;
  }
  
  // Check for allowed allies
  for (const f1 of factions1) {
    const faction1Data = FACTIONS[f1];
    if (faction1Data?.allowedAllies) {
      for (const f2 of factions2) {
        if (faction1Data.allowedAllies.includes(f2)) return 2;
      }
    }
  }
  
  // Check reverse (f2's allowed allies include f1)
  for (const f2 of factions2) {
    const faction2Data = FACTIONS[f2];
    if (faction2Data?.allowedAllies) {
      for (const f1 of factions1) {
        if (faction2Data.allowedAllies.includes(f1)) return 2;
      }
    }
  }
  
  // Same group is NOT enough for coherence
  // A kobold tribe shouldn't include random bandits just because both are humanoids
  // Group membership only provides a small affinity (0.5), not coherence (needs >= 1)
  for (const f1 of factions1) {
    const group1 = FACTION_TO_GROUP[f1];
    for (const f2 of factions2) {
      const group2 = FACTION_TO_GROUP[f2];
      if (group1 && group1 === group2) return 0.5;
    }
  }
  
  return 0;
}

/**
 * Check if two monsters are faction-compatible (score >= 1)
 */
export function areFactionCompatible(monster1, monster2) {
  return getFactionCompatibilityScore(monster1, monster2) >= 1;
}

/**
 * Filter monsters compatible with a given faction (including allies and same group)
 * @param {Object[]} monsters - Array of monsters
 * @param {string} faction - Faction key to filter by
 * @returns {Object[]}
 */
export function filterByFaction(monsters, faction) {
  if (!faction) return monsters;
  
  const factionData = FACTIONS[faction];
  const allowedAllies = factionData?.allowedAllies || [];
  
  return monsters.filter(m => {
    const mFactions = getMonsterFactions(m);
    
    // No factions = excluded (not a free pass)
    if (mFactions.length === 0) return false;
    
    // Same faction
    if (mFactions.includes(faction)) return true;
    
    // Allowed ally (explicitly defined in FACTIONS)
    if (mFactions.some(f => allowedAllies.includes(f))) return true;
    
    // Note: Same faction group is NOT enough - we want thematic coherence
    // A kobold tribe shouldn't include random bandits just because both are humanoids
    
    return false;
  });
}

/**
 * Get the primary faction for an encounter based on monsters
 * Returns the most common faction among the monsters
 */
export function getPrimaryFaction(monsters) {
  const factionCounts = {};
  
  monsters.forEach(m => {
    const factions = getMonsterFactions(m);
    factions.forEach(f => {
      factionCounts[f] = (factionCounts[f] || 0) + 1;
    });
  });
  
  let maxFaction = null;
  let maxCount = 0;
  
  Object.entries(factionCounts).forEach(([faction, count]) => {
    if (count > maxCount) {
      maxCount = count;
      maxFaction = faction;
    }
  });
  
  return maxFaction;
}

/**
 * Check if all monsters in an encounter are compatible
 */
export function isEncounterCoherent(monsters) {
  if (monsters.length <= 1) return true;
  
  for (let i = 0; i < monsters.length; i++) {
    for (let j = i + 1; j < monsters.length; j++) {
      if (getFactionCompatibilityScore(monsters[i], monsters[j]) < 1) {
        return false;
      }
    }
  }
  
  return true;
}

/**
 * Get all unique factions present in a list of monsters
 */
export function getFactionsInList(monsters) {
  const factions = new Set();
  monsters.forEach(m => {
    getMonsterFactions(m).forEach(f => factions.add(f));
  });
  return Array.from(factions);
}

// Legacy function for backwards compatibility
export function getMonsterFaction(monster) {
  const factions = getMonsterFactions(monster);
  return factions.length > 0 ? factions[0] : null;
}

/**
 * Get tactics available for an encounter type
 * @param {string} encounterType - 'boss', 'ambush', or 'waves'
 * @returns {Object[]} Array of tactic objects
 */
export function getTacticsForType(encounterType) {
  return ENCOUNTER_PATTERNS_PER_TYPE[encounterType] || [];
}

/**
 * Pick a tactic for an encounter, biased toward faction preferences
 * @param {string} faction - Faction key (or null)
 * @param {string} encounterType - 'boss', 'ambush', or 'waves'
 * @param {Function} weightedRandomSelect - Weighted selection function
 * @returns {Object|null} Selected tactic object
 */
export function pickTactic(faction, encounterType, weightedRandomSelect) {
  const tacticsForType = getTacticsForType(encounterType);
  
  if (tacticsForType.length === 0) return null;
  
  if (!faction || !FACTIONS[faction]?.preferredTactics) {
    return weightedRandomSelect(tacticsForType);
  }
  
  const preferredNames = FACTIONS[faction].preferredTactics;
  
  // Find tactics that match faction preferences
  const preferred = tacticsForType.filter(tactic => {
    // Check if any pattern name matches preferred tactics
    // Or if the tactic key itself is in preferredTactics
    const tacticKey = Object.keys(TACTICAL_PATTERNS).find(
      key => TACTICAL_PATTERNS[key] === tactic
    );
    return preferredNames.includes(tacticKey);
  });
  
  // 70% chance to use preferred tactic, 30% random
  if (preferred.length > 0 && Math.random() < 0.7) {
    return weightedRandomSelect(preferred);
  }
  
  return weightedRandomSelect(tacticsForType);
}

/**
 * Pick a random pattern from a tactic
 * @param {Object} tactic - Tactic object with patterns array
 * @returns {Object|null} Selected pattern
 */
export function pickPattern(tactic) {
  if (!tactic?.patterns || tactic.patterns.length === 0) return null;
  return tactic.patterns[Math.floor(Math.random() * tactic.patterns.length)];
}

/**
 * Get all preferred roles from a pattern
 * @param {Object} pattern - Pattern object with core, support, flex arrays
 * @returns {string[]} Array of role names
 */
export function getPatternRoles(pattern) {
  if (!pattern) return [];
  return [
    ...(pattern.core || []),
    ...(pattern.support || []),
    ...(pattern.flex || []),
  ];
}

export default {
  FACTIONS,
  FACTION_GROUPS,
  TACTICAL_PATTERNS,
  getMonsterFactions,
  getMonsterFaction,
  getFactionCompatibilityScore,
  areFactionCompatible,
  filterByFaction,
  getPrimaryFaction,
  isEncounterCoherent,
  getFactionsInList,
  getTacticsForType,
  pickTactic,
  pickPattern,
  getPatternRoles,
};
